import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

type ProgressTimerProps = {
	className?: string;
	totalPlayTime?: number;
	gameStarted?: boolean;
	timeAdjustSeconds?: number;
	onTimeOut?: () => void;
};

const ProgressTimer = ({
	className,
	totalPlayTime = 0,
	gameStarted = false,
	timeAdjustSeconds = 0,
	onTimeOut,
}: ProgressTimerProps) => {
	const [timeLeftSeconds, setTimeLeftSeconds] = useState<number>(Math.max(0, Math.floor(totalPlayTime || 0)));

	useEffect(() => {
		setTimeLeftSeconds(Math.max(0, Math.floor(totalPlayTime || 0)));
	}, [totalPlayTime]);

	useEffect(() => {
		if (!gameStarted || (totalPlayTime || 0) <= 0) return;

		const interval = setInterval(() => {
			setTimeLeftSeconds((prev) => Math.max(0, prev - 1));
		}, 1000);

		setTimeLeftSeconds((prev) => Math.max(0, prev - 1));

		return () => clearInterval(interval);
	}, [gameStarted, totalPlayTime]);

	const timeoutTriggeredRef = useRef(false);
	useEffect(() => {
		if (!gameStarted) {
			timeoutTriggeredRef.current = false;
			return;
		}

		if (timeLeftSeconds === 0 && !timeoutTriggeredRef.current) {
			timeoutTriggeredRef.current = true;
			onTimeOut?.();
		}
	}, [timeLeftSeconds, gameStarted, onTimeOut]);

	const prevAdjustRef = useRef<number>(timeAdjustSeconds || 0);
	useEffect(() => {
		const prevAdj = prevAdjustRef.current || 0;
		const nextAdj = timeAdjustSeconds || 0;
		const delta = nextAdj - prevAdj;

		if (delta !== 0) {
			setTimeLeftSeconds((prev) => Math.max(0, prev + delta));
			prevAdjustRef.current = nextAdj;
		}
	}, [timeAdjustSeconds]);

	const label = useMemo(() => {
		const minutes = Math.max(0, Math.floor(timeLeftSeconds / 60));
		const seconds = Math.max(0, timeLeftSeconds % 60);
		return `${minutes}:${String(seconds).padStart(2, '0')}`;
	}, [timeLeftSeconds]);

	return (
		<div className={classNames('progress-timer', className)}>
			<span className="icon icon-clock" />
			{label}
		</div>
	);
};

export default ProgressTimer;
